import {
  type DatadogRum,
  datadogRum,
  type StartRecordingOptions,
  type User,
} from '@datadog/browser-rum';
import {once} from 'lodash';
import {useEffect} from 'react';

import config, {envIsPublicCloud} from '../../config';
import {BUILD_TIME} from '../buildInfo';
import {
  APP_ENTRY_BODY_STARTED_TIMING,
  takeAppEntryBodyStartedTiming,
} from './appEntryBodyTiming';
import {scheduleBrowserGraphicsCapabilities} from './browserGraphicsCapabilities';
import {getBrowserPerformanceContext} from './browserPerformanceContext';
import {
  ALLOWED_TRACING_URLS,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';
import {
  PERF_WINDOW_SAMPLING_VITAL_NAME,
  PERF_WINDOW_VITAL_NAME,
} from './criticalWorkflows/perfWindow/constants';
import type {DatadogMode} from './datadogMode';
import {
  shouldDiscardRumEvent,
  shouldExcludeUrlFromPageActivity,
} from './datadogRumDiscardEvent';
import {getDatadogRum} from './datadogRumFacade';
import {resetRumFeatureFlagEvalsForView} from './datadogRumFlagEval';
import {observeRumInputLatency} from './datadogRumInputLatency';
import {resetRumViewTimings} from './datadogRumTimings';
import {
  claimRumViewOnceAction,
  resetRumViewOnceActionsForView,
} from './datadogRumViewOnceActions';
import {
  hasRumViewEverBeenBackgrounded,
  onRumViewBackgrounded,
  onRumViewPageStatePeriod,
  resetRumViewBackgrounded,
  resetRumViewVisibilityMarks,
  type RumViewMeasuredPageState,
} from './datadogRumViewVisibility';
import {redactRumContext} from './redactRumContext';
import {
  redactGraphQlSensitiveFields,
  redactRumEventUrls,
} from './redactSensitiveFields';

export {getDatadogRum} from './datadogRumFacade';

type RumViewPageStateVitalName = `page_${RumViewMeasuredPageState}`;
// Datadog's duration-vital API treats `name` as the custom vital name and
// `context` as optional enrichment, so the lifecycle state belongs in the name
// for waterfall/readability and remains in context for faceting:
// https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#adddurationvital
// Datadog's RUM docs query custom vitals by `@vital.name`:
// https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/monitoring_page_performance/#custom-vitals
// Local naming choice: use `page_*` because the values come from Chrome's Page
// Lifecycle states, while Datadog's manual `startView()` still attaches the
// vital to the current RUM view:
// https://developer.chrome.com/docs/web-platform/page-lifecycle-api#states
// https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#startview
const RUM_VIEW_PAGE_STATE_VITAL_NAMES: Record<
  RumViewMeasuredPageState,
  RumViewPageStateVitalName
> = {
  passive: 'page_passive',
  hidden: 'page_hidden',
  frozen: 'page_frozen',
};

/**
 * Which RUM mechanism to reach for (view/global context are documented on their
 * wrapper functions below):
 * - Actions (`recordRumAction` below): discrete, user-initiated interactions
 *   (clicks, submits) — not timing data or passive/derived metadata. Names governed by
 *   the action-name allowlist.
 * - Duration vitals (`recordRumDurationVital` below): how long an operation took
 *   (e.g. workspace save). Vitals inherit the current view's context, so prefer these
 *   over stuffing durations into an action payload. `startTime` is an absolute epoch
 *   timestamp (`performance.timeOrigin + <performance.now() reading>`).
 */

/**
 * Records a custom RUM action. The only sanctioned entry point for custom actions
 * (enforced by `wandb/dd-action-use-wrapper`), keeping call sites consistent and
 * leaving one place to evolve behavior (name validation, sampling, redaction, ...)
 * later. Routes through the facade, so actions are skipped when the view has been
 * backgrounded.
 *
 * @param name - the RUM action name
 * @param context - optional per-event context attached to the action
 */
export function recordRumAction(
  name: string,
  context?: Parameters<DatadogRum['addAction']>[1]
): void {
  getDatadogRum().addAction(name, context);
}

/**
 * Records a custom RUM action at most once per RUM view. Use for per-view SLIs
 * ("views with a media panel" vs "views with a media panel error") where a
 * single view with many panels must count as one event.
 *
 * @param name - the RUM action name
 * @param context - optional per-event context attached to the action
 * @param dedupeKey - what counts as "the same" action within a view; defaults
 *   to `name`, extend it when one name is emitted per variant (e.g. per panel type)
 */
export function recordRumActionOncePerView(
  name: string,
  context?: Parameters<DatadogRum['addAction']>[1],
  dedupeKey: string = name
): void {
  if (!claimRumViewOnceAction(dedupeKey)) {
    return;
  }
  recordRumAction(name, context);
}

/**
 * Records a duration vital retrospectively: capture local timestamps, then pass
 * an epoch-anchored `startTime` and the measured `duration`. The only
 * sanctioned duration-vital entry point (enforced by
 * `wandb/dd-vital-use-wrapper`). `startDurationVital`/`stopDurationVital` are
 * disallowed: backgrounding the view between start and stop skews the recorded
 * duration.
 *
 * @param name - the RUM vital name, should be unique
 * @param options - startTime/duration plus optional description and context
 */
export function recordRumDurationVital(
  name: string,
  options: Parameters<DatadogRum['addDurationVital']>[1]
): void {
  // The facade suppresses vitals for the rest of a RUM view after any
  // backgrounding, since hidden-tab scheduling can inflate elapsed timings.
  // Perf collectors track backgrounding per sample. Preserve both marked samples
  // and fresh foreground samples after the user returns to a backgrounded view.
  if (
    name === PERF_WINDOW_VITAL_NAME ||
    name === PERF_WINDOW_SAMPLING_VITAL_NAME
  ) {
    datadogRum.addDurationVital(name, options);
    return;
  }
  getDatadogRum().addDurationVital(name, options);
}

/**
 * Records a handled error to Datadog RUM. The only sanctioned entry point for
 * manual error reports (enforced by `wandb/dd-error-use-wrapper`) — do not call
 * `datadogRum.addError` directly. Errors are intentionally not skipped when the
 * view is backgrounded (unlike actions and timings): an error is meaningful
 * whenever it happens.
 *
 * @param error - the error to report
 * @param context - optional per-event context attached to the error
 */
export function recordRumError(
  error: unknown,
  context?: Parameters<DatadogRum['addError']>[1]
): void {
  getDatadogRum().addError(error, context);
}

/**
 * Sets the RUM user, stamped on every event as `@usr`. The only sanctioned entry
 * point for user identification (enforced by `wandb/dd-user-use-wrapper`).
 * Requires an `id`: the overload of the raw `setUser` that accepts an id-less
 * user is deprecated by the SDK.
 *
 * @param user - user info; `id` is required
 */
export function setRumUser(user: User & {id: string}): void {
  getDatadogRum().setUser(user);
}

/**
 * Starts Session Replay recording. The only sanctioned entry point for
 * controlling replay recording (enforced by `wandb/dd-user-use-wrapper`).
 *
 * Note: with `sessionReplaySampleRate` set and no
 * `startSessionReplayRecordingManually: true` in init, the SDK (v5+) records
 * sampled sessions automatically and this call is a no-op safety net.
 *
 * @param options - e.g. `{force: true}` to record a session sampled out of replay
 */
export function startRumSessionReplayRecording(
  options?: StartRecordingOptions
): void {
  getDatadogRum().startSessionReplayRecording(options);
}

/**
 * Sets a view-context property: a stable, low-cardinality dimension scoped to the
 * current view, stamped on every event for that view and reset when a new view starts.
 * Prefer this (and the `use*RumViewContext` hooks) over `datadogRum.setViewContextProperty`
 * directly; `key` must be allowlisted in `datadogContextKeys.json` (enforced by
 * `wandb/dd-context-property-allowlist`), keeping dashboard keys stable.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumViewContextProperty(key: string, value: unknown): void {
  getDatadogRum().setViewContextProperty(key, value);
}

/**
 * Sets a global/session context property: a session-wide attribute spanning all views,
 * stamped on every event regardless of route. Prefer view context when the value can
 * change as the user navigates; same `datadogContextKeys.json` allowlist applies.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumGlobalContextProperty(key: string, value: unknown): void {
  getDatadogRum().setGlobalContextProperty(key, value);
}

/**
 * Removes a session/global context key. No view-context equivalent exists — view
 * context is cleared automatically on view change.
 *
 * @param key - allowlisted context property key to remove
 */
export function removeRumGlobalContextProperty(key: string): void {
  getDatadogRum().removeGlobalContextProperty(key);
}

/**
 * Sets a global/session context property (session-wide, spans all views) for the
 * hook's lifetime; unset when the property changes or the component unmounts. Prefer
 * view context when the value can change as the user navigates.
 *
 * @param key
 * @param value
 */
export function useRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
      return () => {
        removeRumGlobalContextProperty(key);
      };
    }
    return undefined;
  }, [key, value, shouldLog]);
}

/**
 * As with `useRumGlobalContext` (session-wide attribute spanning all views; prefer
 * view context for values that change on navigation), but does not unset on unmount.
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

/**
 * Sets a view-context property (stable, low-cardinality dimension scoped to the current
 * view; allowlisted in `datadogContextKeys.json`), retained until a new view starts.
 *
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumViewContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumViewContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

const rumViewStartListeners = new Set<() => void>();

/**
 * Runs the listener before each RUM view starts, so in-flight measurements can
 * report under the view they belong to. Returns an unsubscribe function.
 */
export function onRumViewStart(listener: () => void): () => void {
  rumViewStartListeners.add(listener);
  return () => {
    rumViewStartListeners.delete(listener);
  };
}

/**
 * Starts a new Datadog RUM view with the given name.
 * Resets view-specific timings and backgrounded state.
 *
 * @param name - The name of the view/route being started
 */
export function startDatadogRumView(name?: string) {
  for (const listener of rumViewStartListeners) {
    listener();
  }
  const datadogRum = getDatadogRum();

  resetRumViewTimings();
  resetRumViewBackgrounded();
  resetRumViewVisibilityMarks();

  // `startView({name})` sets `@view.name`; filter/group RUM by that instead of a
  // separate (session-global) `routeName` context property.
  datadogRum.startView({name});

  resetRumFeatureFlagEvalsForView();
  resetRumViewOnceActionsForView();

  const appEntryBodyStartedAt = takeAppEntryBodyStartedTiming();
  if (appEntryBodyStartedAt != null) {
    datadogRum.addTiming(APP_ENTRY_BODY_STARTED_TIMING, appEntryBodyStartedAt);
  }
  // Stamp whether this view started hidden (e.g. opened in a background tab, where
  // browsers throttle timers/rendering); the onRumViewBackgrounded subscription in
  // `initDatadogRum` flips it to true if the view is backgrounded later. Dashboards
  // filter `@context.datadogView.was_backgrounded:false` to exclude hidden-tab timing
  // artifacts from percentiles.
  setRumViewContextProperty(
    'datadogView.was_backgrounded',
    hasRumViewEverBeenBackgrounded()
  );
}

/**
 * Initializes Datadog RUM (Real User Monitoring) with configuration for session replay,
 * user tracking, resource monitoring, and custom event filtering.
 * This function is wrapped with `once()` to ensure it only runs once.
 */
export const initDatadogRum = once(function initDatadogRum(mode: DatadogMode) {
  const isServerRum = mode === 'server';

  datadogRum.init({
    // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_resource_performance/#track-graphql-requests
    allowedGraphQlUrls: [
      {
        match: /\/graphql$/,
        trackPayload: true, // Include GraphQL query (limited to 32 KB)
        // Parse the response body's `errors[]` onto the resource event as
        // @resource.graphql.errors (message, path, locations, extensions.code)
        // and @resource.graphql.error_count. The resource event already carries
        // the operation name, status and backend trace id, so this is what
        // says *what* the backend answered for a failed request. The SDK does
        // not let beforeSend modify these fields, so the messages reach
        // Datadog as gorilla sent them to the browser (see README.md).
        trackResponseErrors: true,
      },
    ],
    allowedTracingUrls: [...ALLOWED_TRACING_URLS],
    // v7 defaults propagateTraceBaggage to true, which sends a `baggage` header
    // on traced cross-origin requests. Keep it off until backend CORS allows the
    // header, to avoid breaking traced API calls.
    propagateTraceBaggage: false,

    applicationId: DATADOG_RUM_APPLICATION_ID,
    beforeSend(event) {
      redactRumEventUrls(event);

      if (shouldDiscardRumEvent(event)) {
        return false;
      }

      if (event.type === 'resource') {
        redactGraphQlSensitiveFields(event);
      }

      // Single sanitization point for every event type: per-call context (actions,
      // vitals, errors) and view/global context all merge into event.context by the
      // time beforeSend runs.
      event.context = redactRumContext({
        ...event.context,
        ...(isServerRum ? {'customer-ns': config.CUSTOMER_NS} : {}),
        gitTag: window.CONFIG?.GIT_TAG,
        'build.time': BUILD_TIME,
        envIsPublicCloud,
      });

      return true;
    },
    clientToken: DATADOG_CLIENT_TOKEN,
    // SECURITY: Mask all text in session replays to prevent sensitive data exposure.
    // If you want to see an element in session replay, use data-dd-privacy="allow"
    // on specific elements to selectively unmask non-sensitive content.
    defaultPrivacyLevel: 'mask',
    // With defaultPrivacyLevel: 'mask', clicks report as "Masked Element"
    // unless the element (or an ancestor) has a data-dd-action-name or sits
    // inside a data-dd-privacy="allow" container. Set explicitly (v7 default)
    // so on-screen text — which can include user content like run names —
    // never becomes an action name by accident. To surface an interaction in
    // RUM, give it a registered name (see README.md#stable-action-naming).
    enablePrivacyForActionName: true,
    env: window.CONFIG?.ENVIRONMENT_NAME,
    profilingSampleRate: isServerRum
      ? config.DATADOG_RUM_PROFILING_SAMPLE_RATE
      : 25,
    service: DATADOG_UI_SERVICE,
    version: window.CONFIG?.GIT_TAG,
    sessionSampleRate: isServerRum
      ? config.DATADOG_RUM_SESSION_SAMPLE_RATE
      : 100,
    sessionReplaySampleRate: isServerRum
      ? config.DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE
      : 100,
    site: DATADOG_SITE,
    traceSampleRate: isServerRum ? config.DATADOG_RUM_TRACE_SAMPLE_RATE : 100,
    // propagate feature flag list to additional even types
    trackFeatureFlagsForEvents: ['vital', 'action', 'long_task', 'resource'],
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,
    // Include first-party domains and, in server mode, configured origins.
    // This governs the SDK's page-activity / loading-time computation only;
    // resource *collection* is filtered separately in beforeSend via
    // shouldDiscardRumEvent.
    // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/monitoring_page_performance/#how-page-activity-is-calculated
    excludedActivityUrls: [shouldExcludeUrlFromPageActivity],
    trackViewsManually: true,
  });

  observeRumInputLatency(recordRumDurationVital);

  // Reuse input_latency's validation, but pass an empty performance snapshot
  // so browser context contains capacity fields only and never reads live heap.
  const {deviceMemoryGiB, hardwareConcurrency} = getBrowserPerformanceContext(
    navigator,
    {}
  );
  if (deviceMemoryGiB != null) {
    setRumGlobalContextProperty('browser.deviceMemoryGiB', deviceMemoryGiB);
  }
  if (hardwareConcurrency != null) {
    setRumGlobalContextProperty(
      'browser.hardwareConcurrency',
      hardwareConcurrency
    );
  }

  setRumGlobalContextProperty('browser.probeStatus', 'pending');
  scheduleBrowserGraphicsCapabilities(capabilities => {
    for (const [key, value] of Object.entries(capabilities)) {
      setRumGlobalContextProperty(`browser.${key}`, value);
    }
  });

  // Keep `@context.datadogView.was_backgrounded` (stamped false-or-hidden at each
  // `startDatadogRumView`) truthful for the rest of the view: flip it the first time
  // the tab goes hidden. The `skipFnIfHidden` facade already drops *new* timings once
  // backgrounded, but events recorded before backgrounding — and SDK-computed view
  // metrics like `loading_time` on background-opened tabs — still ship; this property
  // is what lets dashboards filter those out. (`datadogRumViewVisibility` owns the
  // underlying visibilitychange listener.)
  onRumViewBackgrounded(() => {
    setRumViewContextProperty('datadogView.was_backgrounded', true);
  });

  // Record completed passive/hidden/frozen page-state periods as state-specific
  // duration vitals with Datadog-supported `startTime`, `duration`, and
  // `context` options:
  // https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#adddurationvital
  //
  // Evidence for the lifecycle mapping and exclusions lives with
  // RumViewMeasuredPageState in `datadogRumViewVisibility.ts`. Local naming
  // choice: the `page_*` prefix matches Chrome's Page Lifecycle vocabulary and
  // avoids overloading Datadog's RUM view terminology; the vital still attaches
  // to the current RUM view via Datadog's manual view tracking.
  onRumViewPageStatePeriod(({state, startMark, durationMs}) => {
    // Intentionally call the raw SDK method so these lifecycle-correction
    // vitals are not dropped by the hidden-view facade they are meant to
    // explain.
    datadogRum.addDurationVital(RUM_VIEW_PAGE_STATE_VITAL_NAMES[state], {
      startTime: performance.timeOrigin + startMark,
      duration: durationMs,
      // Maps 1:1 to the completed Chrome Page Lifecycle state period:
      // `passive`, `hidden`, or `frozen`.
      context: {pageState: state},
    });
  });
});
